// Components==============
import {
  faAngry,
  faChartBar,
  faDizzy,
  faFrownOpen,
  faLaughBeam,
  faMeh,
  faPalette,
  faSmile,
  faSmileBeam,
  faSurprise,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { AnimatePresence, motion } from 'framer-motion';
import { useProcessGroups } from 'hooks/useProcessGroups';
import React, { useContext, useState } from 'react';
import { useHistory, useParams } from 'react-router';
import { GlobalReflectionContext } from 'utilities/GlobalContext/GlobalReflectionContext';
import CircleButton from '../CircleButton/CircleButton';
import Card from './Card/Card';
import './ProgressGraph.module.scss';
// =========================

const variants = {
  hover: {},
  initial: {},
};

const bar = {
  hover: {
    opacity: 0.2,
  },
  initial: {
    opacity: 0,
  },
};

export default function ProgressGraph({
  data,
  topLeft,
  project,
  saveKey,
}: {
  data: (ReflectionEntity | 'filler' | undefined)[];
  topLeft: React.ReactNode;
  project?: ProjectProjectEntity;
  saveKey?: number;
}) {
  const [showPhase, setShowPhase] = useState(true);
  const [showEmoji, setShowEmoji] = useState(true);
  const [showBars, setShowBars] = useState(true);

  const { setSelectedReflection } = useContext(GlobalReflectionContext);
  const history = useHistory();
  const params = useParams() as { projectId?: string };

  const horizontalBars = Array.from(Array(8).keys());

  const { processGroups } = useProcessGroups(project, saveKey);

  return (
    <div styleName="wrapper">
      <div styleName="top">
        {topLeft}
        <div styleName="buttons">
          <CircleButton
            icon={faChartBar}
            onClick={() => setShowBars((prev) => !prev)}
            color={showBars ? 'blue' : 'grey'}
          />
          <CircleButton
            icon={faSmile}
            onClick={() => setShowEmoji((prev) => !prev)}
            color={showEmoji ? 'blue' : 'grey'}
          />
          <CircleButton
            icon={faPalette}
            onClick={() => setShowPhase((prev) => !prev)}
            color={showPhase ? 'blue' : 'grey'}
          />
        </div>
      </div>
      <div styleName="bottom">
        <div styleName="graph">
          {horizontalBars.map((e, i) => (
            <div
              key={e}
              styleName="horizontal-bar"
              style={{
                gridRow: i + 1,
                gridColumn: `1/${data?.length + 1}`,
                borderRadius:
                  i === 0 ? '7px 7px 0 0' : i === 7 ? '0 0 7px 7px' : '',
              }}
            />
          ))}
          {data.map((reflection, i) => {
            if (reflection === 'filler' || !reflection?.contributed)
              return (
                <div style={{ gridColumn: i + 1, minWidth: 40 }} key={i} />
              );

            const phase = processGroups.find(
              (p) =>
                !!p.activities.find(
                  (a) => a.processId === reflection?.project_process?.id
                )
            )?.phase;

            return (
              <motion.div
                key={i}
                styleName="vertical-bar"
                whileHover="hover"
                initial="initial"
                variants={variants}
                style={{ gridColumn: i + 1 }}
                onClick={() => {
                  setSelectedReflection(reflection.id);
                  if (
                    project?.id &&
                    parseInt(params?.projectId || '0') !== project.id
                  )
                    history.push(`/groei/reflecteren/${project?.id}`);
                }}
              >
                {reflection?.emotions.map((emotion) => (
                  <div
                    key={emotion.id}
                    styleName="emoji-level"
                    style={{
                      gridRow: emotion.order,
                      opacity: showEmoji ? 1 : 0,
                    }}
                  />
                ))}
                <AnimatePresence>
                  {showBars && reflection?.result && (
                    <motion.div
                      initial={{ y: 5, opacity: 0 }}
                      exit={{ y: 5, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      styleName="bar"
                      style={{
                        gridRow: reflection?.result
                          ? `9/${9 - reflection.result}`
                          : '0',
                      }}
                      transition={{ delay: i * 0.05 }}
                    >
                      <div
                        styleName={`inner-bar ${showPhase ? phase : ''} ${
                          reflection?.result === 8 ? 'top' : ''
                        }`}
                      />
                    </motion.div>
                  )}
                </AnimatePresence>
                <motion.div styleName="bar-overlay" variants={bar} />
                {!!reflection && <Card reflection={reflection} />}
              </motion.div>
            );
          })}
        </div>
        <div styleName="emoji-bar">
          <FontAwesomeIcon icon={faLaughBeam} />
          <FontAwesomeIcon icon={faSmileBeam} />
          <FontAwesomeIcon icon={faSmile} />
          <FontAwesomeIcon icon={faMeh} />
          <FontAwesomeIcon icon={faSurprise} />
          <FontAwesomeIcon icon={faDizzy} />
          <FontAwesomeIcon icon={faFrownOpen} />
          <FontAwesomeIcon icon={faAngry} />
        </div>
      </div>
    </div>
  );
}
